import React, { useState } from 'react';
import { Navigate } from 'react-router-dom';
import { Clock, CheckCircle, XCircle, MessageCircle, User, Calendar, DollarSign, FileText, Briefcase, Star, Mail, Phone } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useLeaseStore } from '../stores/leaseStore';
import { useMessageStore } from '../stores/messageStore';
import { getProfilePicture } from '../utils/profileUtils';
import ApprovalConfirmation from '../components/ApprovalConfirmation';
import { formatPrice, formatDate } from '../utils/formatters';

const LeaseRequests = () => {
  const { user } = useAuth();
  const { requests, updateRequestStatus } = useLeaseStore();
  const { createConversation } = useMessageStore();
  const [selectedRequest, setSelectedRequest] = useState<string | null>(null);
  const [showApprovalConfirmation, setShowApprovalConfirmation] = useState(false);

  if (!user) {
    return <Navigate to="/login" />;
  }

  // Only show incoming requests for owners
  if (user.role !== 'owner') {
    return <Navigate to="/dashboard" />;
  }

  // Get only requests for listings owned by this user
  const incomingRequests = requests.filter(request => 
    request.listing.ownerId === user.id
  ).sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());

  const handleMessage = (request: any) => {
    // Create or get conversation
    const conversationId = createConversation({
      participants: [user.id, request.applicant.id],
      recipientId: request.applicant.id,
      recipientName: request.applicant.name,
      listingId: request.listingId,
      listingTitle: request.listing.title
    });
    navigate(`/messages?conversation=${conversationId}`);
  };

  const handleApprove = (requestId: string) => {
    setSelectedRequest(requestId);
    setShowApprovalConfirmation(true);
  };

  const handleReject = (requestId: string) => {
    updateRequestStatus(requestId, { status: 'rejected' });
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'approved':
        return 'bg-green-100 text-green-800';
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      case 'rejected':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'approved':
        return 'Approved';
      case 'pending':
        return 'Pending Review';
      case 'rejected':
        return 'Rejected';
      default:
        return 'Unknown';
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-8">
          <h1 className="text-2xl font-bold text-gray-900">Incoming Lease Applications</h1>
          <p className="text-gray-600">Review and manage applications for your properties</p>
        </div>

        {incomingRequests.length === 0 ? (
          <div className="bg-white rounded-lg shadow-sm p-8 text-center">
            <Clock className="mx-auto h-12 w-12 text-gray-400 mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No Applications Yet</h3>
            <p className="text-gray-600">When you receive lease applications, they will appear here.</p>
          </div>
        ) : (
          <div className="space-y-6">
            {incomingRequests.map((request) => {
              const profilePic = getProfilePicture(request.applicant.id);
              const showContactInfo = request.status === 'approved' && request.depositPaid;

              return (
                <div key={request.id} className="bg-white rounded-lg shadow-sm overflow-hidden">
                  {/* Request Header */}
                  <div className="p-6 border-b border-gray-200">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center">
                        <div className="h-12 w-12 rounded-full overflow-hidden bg-gray-200">
                          <img 
                            src={profilePic} 
                            alt={request.applicant.name}
                            className="h-full w-full object-cover"
                          />
                        </div>
                        <div className="ml-4">
                          <h3 className="text-lg font-medium text-gray-900">
                            {request.applicant.name}
                          </h3>
                          <p className="text-gray-600">
                            For: {request.listing.title}
                          </p>
                        </div>
                      </div>
                      <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(request.status)}`}>
                        {getStatusText(request.status)}
                      </span>
                    </div>
                  </div>

                  {/* Request Details */}
                  <div className="p-6">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
                      <div>
                        <div className="flex items-center text-sm text-gray-500 mb-1">
                          <Calendar className="h-4 w-4 mr-2" />
                          Requested Period
                        </div>
                        <p className="font-medium">
                          {formatDate(request.moveInDate)} - {formatDate(request.moveOutDate)}
                        </p>
                      </div>

                      {showContactInfo && (
                        <>
                          <div>
                            <div className="flex items-center text-sm text-gray-500 mb-1">
                              <Mail className="h-4 w-4 mr-2" />
                              Contact Email
                            </div>
                            <p className="font-medium">{request.applicant.email}</p>
                          </div>

                          <div>
                            <div className="flex items-center text-sm text-gray-500 mb-1">
                              <Phone className="h-4 w-4 mr-2" />
                              Phone Number
                            </div>
                            <p className="font-medium">{request.applicant.phone}</p>
                          </div>
                        </>
                      )}

                      <div>
                        <div className="flex items-center text-sm text-gray-500 mb-1">
                          <Briefcase className="h-4 w-4 mr-2" />
                          Occupation
                        </div>
                        <p className="font-medium">{request.applicant.occupation}</p>
                      </div>

                      <div>
                        <div className="flex items-center text-sm text-gray-500 mb-1">
                          <DollarSign className="h-4 w-4 mr-2" />
                          Monthly Income
                        </div>
                        <p className="font-medium">{formatPrice(request.applicant.monthlyIncome)}</p>
                      </div>

                      <div>
                        <div className="flex items-center text-sm text-gray-500 mb-1">
                          <Star className="h-4 w-4 mr-2" />
                          Income to Rent Ratio
                        </div>
                        <p className="font-medium">
                          {(request.applicant.monthlyIncome / request.listing.price).toFixed(1)}x
                        </p>
                      </div>
                    </div>

                    {/* Message */}
                    {request.message && (
                      <div className="mb-6">
                        <h4 className="text-sm font-medium text-gray-900 mb-2">Message</h4>
                        <p className="text-gray-600 text-sm whitespace-pre-wrap">{request.message}</p>
                      </div>
                    )}

                    {/* Actions */}
                    <div className="flex justify-end space-x-4">
                      <button
                        onClick={() => handleMessage(request)}
                        className="flex items-center px-4 py-2 text-indigo-600 hover:text-indigo-700"
                      >
                        <MessageCircle className="h-5 w-5 mr-2" />
                        Message Applicant
                      </button>

                      {request.status === 'pending' && (
                        <>
                          <button
                            onClick={() => handleReject(request.id)}
                            className="flex items-center px-4 py-2 text-gray-700 hover:text-gray-900"
                          >
                            <XCircle className="h-5 w-5 mr-2" />
                            Reject
                          </button>
                          <button
                            onClick={() => handleApprove(request.id)}
                            className="flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
                          >
                            <CheckCircle className="h-5 w-5 mr-2" />
                            Approve
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Approval Confirmation Modal */}
        {showApprovalConfirmation && selectedRequest && (
          <ApprovalConfirmation
            request={requests.find(r => r.id === selectedRequest)}
            onConfirm={(data) => {
              updateRequestStatus(selectedRequest, { 
                status: 'approved',
                ...data
              });
              setShowApprovalConfirmation(false);
              setSelectedRequest(null);
            }}
            onCancel={() => {
              setShowApprovalConfirmation(false);
              setSelectedRequest(null);
            }}
          />
        )}
      </div>
    </div>
  );
};

export default LeaseRequests;